import React, { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Close } from '@/components/Icons/Close';
import { Modal } from '@/components/Modal';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { ButtonVariant, ModalVariant, Size } from '@/types/components';
import { applyLink, getSelectedText } from '@/utils/editor';

const inputClassName =
  'h-11 rounded-xl bg-on-primary-surface pr-5 pl-4 text-body-16 text-foreground outline-none placeholder:text-subtle-foreground';

interface Props {
  editor: Editor | null;
  isOpen: boolean;
  onClose: () => void;
}

export const LinkModal: React.FC<Props> = ({ editor, isOpen, onClose }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const { sheetRef, swipeProps } = useSwipeToClose(onClose);

  useEffect(() => {
    if (!isOpen || !editor) {
      return;
    }

    setUrl(editor.getAttributes('link').href ?? '');
    setText(getSelectedText(editor));
  }, [isOpen, editor]);

  const handleConfirm = () => {
    if (!editor) {
      return;
    }

    applyLink(editor, url, text);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      label={t('mail.composeModal.linkModal.title')}
      variant={ModalVariant.BottomSheet}
      className="md:h-auto md:w-[400px] md:rounded-2xl"
      contentRef={sheetRef}
      onClose={onClose}
    >
      <div className="relative flex flex-col gap-6 p-6 pb-[calc(24px+env(safe-area-inset-bottom))] max-md:pb-[calc(32px+env(safe-area-inset-bottom))]">
        <div
          {...swipeProps}
          className="absolute inset-x-0 top-0 flex h-6 justify-center md:hidden max-md:cursor-grab max-md:touch-none max-md:active:cursor-grabbing"
        >
          <div className="mt-2 h-[3px] w-10 rounded-full bg-drag-handle" />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('global.close')}
          className="absolute top-3 right-3 hidden size-4 cursor-pointer items-center justify-center text-foreground md:flex"
        >
          <Close />
        </button>

        <h2 className="text-[18px]/[24px] font-semibold text-foreground md:text-[20px]/[28px] md:font-medium">
          {t('mail.composeModal.linkModal.title')}
        </h2>

        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-2">
            <span className="text-body-16 text-foreground">
              {t('mail.composeModal.linkModal.address')}
            </span>
            <input
              value={url}
              placeholder={t('mail.composeModal.linkModal.addressPlaceholder')}
              onChange={(event) => setUrl(event.target.value)}
              className={inputClassName}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-body-16 text-foreground">
              {t('mail.composeModal.linkModal.text')}
            </span>
            <input
              value={text}
              placeholder={t('mail.composeModal.linkModal.textPlaceholder')}
              onChange={(event) => setText(event.target.value)}
              className={inputClassName}
            />
          </label>
        </div>

        <div className="flex gap-3">
          <Button
            className="flex-1"
            variant={ButtonVariant.Secondary}
            size={Size.Large}
            onClick={onClose}
          >
            {t('mail.composeModal.linkModal.cancel')}
          </Button>
          <Button
            className="flex-1"
            variant={ButtonVariant.Primary}
            size={Size.Large}
            onClick={handleConfirm}
          >
            {t('mail.composeModal.linkModal.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
